import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import gtSemver from 'semver/functions/gt';
import coerceSemver from 'semver/functions/coerce';
import { APP_VERSION } from 'src/environments/version';
import { AuthenticationService } from './authentication.service';
import { NotificationService } from './notification.service';
import { ModalService } from './modal.service';
import { AppVersionInfo } from '../models/app-release';

/**
 * "Is there a newer Conductor than the one I'm running?"
 *
 * `current` is baked in at build time (src/environments/version.ts, from
 * package.json). `latest` comes from the server's release record and is
 * refreshed on login, on tab-return / window focus (throttled), and the
 * moment an AppSync `onNotifyNewAppVersion` broadcast arrives.
 *
 * When latest > current the update banner shows until the page is reloaded
 * onto the new build. If the release was sent as an ALERT, the release-notes
 * modal also opens by itself — once per version per tab; after that the
 * banner carries it. There is no snooze timer any more: the banner IS the
 * reminder.
 */
@Injectable({
  providedIn: 'root',
})
export class AppVersionService {
  /** Version of the build this tab is running. */
  readonly current = APP_VERSION;

  private static readonly CHECK_INTERVAL_MS = 60 * 1000;

  private latestSource = new BehaviorSubject<AppVersionInfo | null>(null);
  readonly latest$: Observable<AppVersionInfo | null> = this.latestSource.asObservable();

  readonly updatePending$: Observable<boolean> = this.latest$.pipe(
    map((latest) => this.isNewerThanCurrent(latest?.version)),
    distinctUntilChanged()
  );

  private lastCheckAt = 0;
  private inFlight = false;
  private alertedForVersion: string | null = null;

  constructor(
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
    private modalService: ModalService
  ) {
    // The check needs a JWT; run it as soon as there is one (login or a
    // verified token on boot), and again after every re-authentication.
    this.authenticationService.isAuthenticated
      .pipe(filter((isAuthenticated) => isAuthenticated === true))
      .subscribe(() => this.check(true));
  }

  get latest(): AppVersionInfo | null {
    return this.latestSource.value;
  }

  get updatePending(): boolean {
    return this.isNewerThanCurrent(this.latest?.version);
  }

  /** Strict semver compare; anything unparseable is "not newer". */
  isNewerThanCurrent(version: string | null | undefined): boolean {
    return this.isNewer(version, this.current);
  }

  isNewer(a: string | null | undefined, b: string | null | undefined): boolean {
    const va = coerceSemver(a ?? '');
    const vb = coerceSemver(b ?? '');
    if (!va || !vb) return false;
    return gtSemver(va, vb);
  }

  /**
   * Ask the server what the latest release is. Throttled to once a minute
   * unless forced (a push, a login) so a tab that flips focus repeatedly
   * doesn't hammer the API. Errors are swallowed: the check is advisory and
   * must never break the page.
   */
  check(force = false): void {
    if (this.authenticationService.isAuthenticatedValue !== true) return;

    const now = Date.now();
    if (!force && now - this.lastCheckAt < AppVersionService.CHECK_INTERVAL_MS) return;
    if (this.inFlight) return;

    this.inFlight = true;
    this.lastCheckAt = now;

    this.notificationService.getAppVersion().subscribe({
      next: (info) => {
        this.inFlight = false;
        this.latestSource.next(info);
        this.maybeAlert(info);
      },
      error: () => {
        this.inFlight = false;
      },
    });
  }

  /**
   * AppSync `onNotifyNewAppVersion` — the broadcast says only "version X
   * exists". Fetch the real record (title, alert flag, id) rather than trust
   * the payload; the old Postman route still fires this with nothing behind it.
   */
  onPush(versionInfo: { version?: string }): void {
    if (!this.isNewerThanCurrent(versionInfo?.version)) return;
    this.check(true);
  }

  openReleaseNotes(releaseId: string): void {
    this.notificationService.getRelease(releaseId).subscribe({
      next: (release) =>
        this.modalService.releaseNotes({
          release,
          updatePending: this.isNewerThanCurrent(release.version),
          fullscreen: release.fullscreen === true,
        }),
      error: (err) => this.modalService.error(err),
    });
  }

  /**
   * The client is static assets on S3 + CloudFront with `no-cache` and a
   * full invalidation per deploy, and there is no service worker — a plain
   * reload fetches the new index.html and its hashed bundles.
   */
  reload(): void {
    document.location.reload();
  }

  private maybeAlert(info: AppVersionInfo): void {
    if (!info || !info.alert || !info.id || !info.version) return;
    if (!this.isNewerThanCurrent(info.version)) return;
    if (this.alertedForVersion === info.version) return;

    this.alertedForVersion = info.version;
    this.openReleaseNotes(info.id);
  }
}
